/**
 * The confidence threshold and the ICP description, as recipes — Story 20.4.
 *
 * Recipes for `edit()`, kept out of the component for the reason
 * `editor/rubric.ts` gives: every change is a patch undo covers, and all of it is
 * testable without a store or a browser. The bounds are the schema's own —
 * `CONFIDENCE_THRESHOLD_DEFAULT` and `ICP_PROFILE_MAX` live in
 * `packages/schema/src/rubric.ts` beside the fields they bound.
 */
import { CONFIDENCE_THRESHOLD_DEFAULT, ICP_PROFILE_MAX, applyOperation, type Funnel } from '@leadgible/schema'
import { COMPOSED } from './derived-copy'
import { fitReaders } from './derived-criterion'
import { DRAWN } from './escalation-copy'
import { joinNames } from './extraction'

/**
 * The funnel's threshold.
 *
 * The parsed type always carries one, because the schema defaults it. What
 * reaches this tab is JSON, though, and a draft served by an API older than Story
 * 20.4 has no such key — so absence reads as the default the schema would have
 * given it, never as "undefined%". The same stance the tab takes on an `ai` field
 * an older API does not send.
 */
export function thresholdOf(funnel: Funnel): number {
  const stored = (funnel.rubric as { confidence_threshold?: number }).confidence_threshold
  return typeof stored === 'number' ? stored : CONFIDENCE_THRESHOLD_DEFAULT
}

/** Sets a whole percent from 0 to 100. Anything else is not a threshold, and the field does not move. */
export function setConfidenceThreshold(draft: Funnel, value: number): void {
  if (!Number.isInteger(value) || value < 0 || value > 100) return
  draft.rubric.confidence_threshold = value
}

/**
 * Why the description cannot be emptied, or null when it can — Story 20.9.
 *
 * The rubric refuses a `derived` criterion over an empty `icp_profile`, because
 * the fit it reads is measured against the description. So emptying it while a
 * criterion reads the fit writes a rubric the next autosave refuses; the refusal
 * happens here, before the edit, naming the criterion — the treatment Story 20.3
 * gave a field a criterion scores. Whitespace is empty: the rubric trims.
 */
export function icpRefusal(funnel: Funnel, text: string): string | null {
  if (text.trim() !== '') return null
  const labels = fitReaders(funnel).map((criterion) => criterion.label)
  if (labels.length === 0) return null
  if (labels.length === 1) return DRAWN.icpRefused(labels[0]!)
  return COMPOSED.icpRefusedMany(joinNames(labels))
}

/**
 * Writes the ICP description, cut at the schema's own bound rather than past it —
 * the vocabulary's `set_icp_profile` since Story 27.5, the operation a model's
 * proposal calls (RULE-019). It refuses what `icpRefusal` refuses, emptying the
 * description while a criterion reads the fit, and then nothing changes and
 * nothing is saved. The cut is this door's: a person's paste is trimmed to fit,
 * where a model's over-long description is refused.
 */
export function setIcpProfile(draft: Funnel, text: string): void {
  applyOperation(draft, { op: 'set_icp_profile', description: text.slice(0, ICP_PROFILE_MAX) })
}

/**
 * The band the consequence sentence calls confident — the highest one.
 *
 * The drawn sentence says Hot because the demo's top band is called Hot. An
 * operator's top band can be called anything ("Exceptional", "Book a call"), and
 * the sentence has to name the band this funnel actually routes on.
 */
export function topBandLabel(funnel: Funnel): string {
  const [top] = [...funnel.rubric.bands].sort((a, b) => b.min - a.min)
  return top?.label ?? ''
}
